import { useCallback, useContext, useSyncExternalStore } from "react";

import { devWarn } from "../modules/devWarn";

import { HallidayContext } from "./context";
import type { UseHallidayPaymentsResult, WalletParams } from "./types";

/**
 * Subscribe to the Provider's instance and read its reactive snapshot. Returns the snapshot
 * fields (`isReady`/`isOpen`/`status`/`error`) plus the instance-bound entry points, `updateWallets`, and the
 * instance itself.
 *
 * The snapshot is read through `useSyncExternalStore(core.subscribe, core.getSnapshot)`; `core.getSnapshot`
 * returns a referentially-stable object between unchanged reads, so React only re-renders on a real transition.
 */
export const useHallidayPayments = (): UseHallidayPaymentsResult => {
    const ctx = useContext(HallidayContext);
    if (!ctx) {
        throw new Error("useHallidayPayments must be used within a <HallidayPaymentsProvider>.");
    }
    const { core, configAddrRef } = ctx;

    const snapshot = useSyncExternalStore(core.subscribe, core.getSnapshot, core.getSnapshot);

    // Forward the imperative wallet slice straight to updateConfig (normalize→shallow-merge handles the
    // flattening + role-change re-resolution). Dev-warn when a destinationAddress is also set declaratively.
    const updateWallets = useCallback(
        (wallets: WalletParams): void => {
            const flags = configAddrRef.current;
            if (flags.deposit && wallets.deposit?.destinationAddress != null) {
                devWarn(
                    "[HallidayPayments] deposit.destinationAddress is set via both Provider props and " +
                        "updateWallets — the updateWallets value wins. Set it in one place.",
                );
            }
            if (flags.withdrawal && wallets.withdrawal?.destinationAddress != null) {
                devWarn(
                    "[HallidayPayments] withdrawal.destinationAddress is set via both Provider props and " +
                        "updateWallets — the updateWallets value wins. Set it in one place.",
                );
            }
            core.updateConfig(wallets);
        },
        [core, configAddrRef],
    );

    return {
        ...snapshot,
        openDeposit: core.openDeposit,
        openWithdrawal: core.openWithdrawal,
        openActivity: core.openActivity,
        openOrder: core.openOrder,
        close: core.close,
        updateWallets,
        instance: core,
    };
};
